import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { MoreHorizontal } from "lucide-react"

import { cn } from "@/lib/utils"

/**
 * Padding on a single breadcrumb entry — 8px on all four sides (--spacing-2).
 *
 * Exported because it is what gives a crumb its height: a 20px label line box plus 8px
 * top and bottom is the 36px row the trail actually occupies. Anything that has to
 * RESERVE that height — the loading skeleton in
 * `category-breadcrumbs/skeleton.tsx` — composes this same constant rather than
 * restating `p-2` or hard-coding `h-9`, so the reserved space is the space eventually
 * taken instead of an approximation that drifts when the padding changes.
 */
export const BREADCRUMB_ENTRY_PADDING = "p-2"

function Breadcrumb({ ...props }: React.ComponentProps<"nav">) {
  return <nav aria-label="breadcrumb" data-slot="breadcrumb" {...props} />
}

function BreadcrumbList({ className, ...props }: React.ComponentProps<"ol">) {
  return (
    <ol
      data-slot="breadcrumb-list"
      className={cn(
        // Figma `components/label-sm`: 14px / 20px line-height / 1.4px tracking / weight 450.
        // `text-sm` alone resolves to a 1.5 ratio (21px), which is the app-wide body/sm style,
        // so the 20px line-height is set explicitly here. Tracking follows the repo convention
        // that Figma letterSpacing is percent -> em (tailwind.css:43), so 10 -> 0.1em = 1.4px at
        // 14px. `font-book` is SRAM 55 Roman (450).
        //
        // `text-transform: capitalize` is deliberately NOT applied even though label-sm
        // specifies it: it uppercases the first letter of every word, so SRAM's own
        // lowercase-initial brand names break — "eTap AXS XPLR" renders as "ETap AXS XPLR".
        // Category labels come from the catalogue already cased correctly (decision 2026-08-05).
        // `gap-1` — 4px between list items (decision 2026-08-12).
        //
        // Note this is ON TOP of the entries' own padding, not instead of it: each link and the
        // current-page entry carry `p-2` (which is also what draws the hover chip), and the
        // separator is a list item in its own right. So between a label and its separator the
        // total is 8px of padding plus this 4px. The stock shadcn value is `gap-2`, which was
        // removed earlier in CRCB2B-87 for reading as a loose trail at that combined spacing;
        // 4px is the deliberate middle setting, not a revert to the stock value.
        "text-muted-foreground font-book flex flex-wrap items-center gap-1 text-sm leading-5 tracking-[0.1em] break-words",
        className
      )}
      {...props}
    />
  )
}

function BreadcrumbItem({ className, ...props }: React.ComponentProps<"li">) {
  return (
    <li
      data-slot="breadcrumb-item"
      className={cn("inline-flex items-center gap-1.5", className)}
      {...props}
    />
  )
}

function BreadcrumbLink({
  asChild,
  className,
  ...props
}: React.ComponentProps<"a"> & {
  asChild?: boolean
}) {
  const Comp = asChild ? Slot : "a"

  return (
    <Comp
      data-slot="breadcrumb-link"
      // 8px on all four sides (--spacing-2, Tailwind `2` on the 4px scale). The padding is
      // ALWAYS present, never applied under `hover:` — it is what `bg-accent` reveals, not
      // something hover adds. Putting it on the hover state would jump the whole trail
      // sideways as the pointer crosses an entry.
      // Figma's dev-mode CSS reads `padding: var(--spacing-2) var(--spacing-xs)` — 8px block,
      // 4px inline — but the design owner confirmed 8px on all sides for every breadcrumb,
      // base state included, on both PDP and PLP (decision 2026-08-06).
      className={cn(
        "hover:bg-accent",
        BREADCRUMB_ENTRY_PADDING,
        "transition-colors",
        className
      )}
      {...props}
    />
  )
}

function BreadcrumbPage({ className, ...props }: React.ComponentProps<"span">) {
  return (
    <span
      data-slot="breadcrumb-page"
      role="link"
      aria-disabled="true"
      aria-current="page"
      // No weight of its own: the design's current entry is the same `label-sm` as the
      // others (weight 450, inherited from the list) and differs only in colour. The
      // previous `font-normal` (400) undercut it.
      // Padding matches BreadcrumbLink's 8px exactly: the current entry carries no hover
      // chip, but an entry shorter than its neighbours would sit visibly off against the
      // list's `items-center`, and the row height would change between PLP (current entry
      // last) and PDP (link last).
      className={cn("text-foreground", BREADCRUMB_ENTRY_PADDING, className)}
      {...props}
    />
  )
}

function BreadcrumbSeparator({
  children,
  className,
  ...props
}: React.ComponentProps<"li">) {
  return (
    <li
      data-slot="breadcrumb-separator"
      role="presentation"
      aria-hidden="true"
      className={cn(
        "text-lg leading-none select-none [&>svg]:size-3.5",
        className
      )}
      {...props}
    >
      {children ?? "/"}
    </li>
  )
}

function BreadcrumbEllipsis({
  className,
  ...props
}: React.ComponentProps<"span">) {
  return (
    <span
      data-slot="breadcrumb-ellipsis"
      role="presentation"
      aria-hidden="true"
      className={cn("flex size-9 items-center justify-center", className)}
      {...props}
    >
      <MoreHorizontal className="size-4" />
      <span className="sr-only">More</span>
    </span>
  )
}

export {
  Breadcrumb,
  BreadcrumbList,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbPage,
  BreadcrumbSeparator,
  BreadcrumbEllipsis,
}
